import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { authDownloadUrl } from '../api/client';
import { useAuth } from '../context/AuthContext';

/**
 * Live updates: one EventSource per tab on /api/live/stream. Each event names a topic
 * ("meetings", "jobs", "automation", "notifications" …); pages subscribe with useLive(topics, reload)
 * and reload their data quietly — no page refresh. Notifications also arrive here (bell + pop-up).
 */
const LiveContext = createContext(null);

export function LiveProvider({ children }) {
  const { user } = useAuth();
  const subscribers = useRef(new Set());
  const notificationListeners = useRef(new Set());
  const [connected, setConnected] = useState(false);

  const emit = useCallback((topic, event) => {
    subscribers.current.forEach((s) => {
      if (topic === '*' || s.topics.includes('*') || s.topics.includes(topic)) s.fire(event);
    });
    if (topic === 'notifications' || topic === '*') notificationListeners.current.forEach((fn) => fn(event));
  }, []);

  useEffect(() => {
    if (!user?.id || typeof EventSource === 'undefined') return undefined;
    let source = null;
    let closed = false;
    let hellos = 0;
    let retryTimer = null;
    const open = () => {
      source = new EventSource(authDownloadUrl('/api/live/stream'));
      source.addEventListener('hello', () => {
        setConnected(true);
        // A new connection after the first: changes may have been missed meanwhile — reload everything.
        if (hellos++ > 0) emit('*', { topic: '*' });
      });
      source.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);
          emit(event.topic, event);
        } catch {
          /* ignore malformed events */
        }
      };
      source.onerror = () => {
        setConnected(false);
        // EventSource retries by itself; if the session expired it gives up (CLOSED) — try again later.
        if (source.readyState === EventSource.CLOSED && !closed) {
          retryTimer = setTimeout(open, 15000);
        }
      };
    };
    open();
    return () => {
      closed = true;
      clearTimeout(retryTimer);
      if (source) source.close();
    };
  }, [user?.id, emit]);

  const subscribe = useCallback((topics, fire) => {
    const entry = { topics, fire };
    subscribers.current.add(entry);
    return () => subscribers.current.delete(entry);
  }, []);

  const onNotification = useCallback((fn) => {
    notificationListeners.current.add(fn);
    return () => notificationListeners.current.delete(fn);
  }, []);

  return (
    <LiveContext.Provider value={{ subscribe, onNotification, connected }}>
      {children}
    </LiveContext.Provider>
  );
}

export function useLiveStatus() {
  return useContext(LiveContext)?.connected || false;
}

/**
 * Call `reload` when something in `topics` changes (debounced). While the tab is hidden the reload
 * waits until it is shown again. `reload` should refresh quietly (no loading spinner).
 */
export function useLive(topics, reload, { delay = 600, enabled = true } = {}) {
  const ctx = useContext(LiveContext);
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const key = Array.isArray(topics) ? topics.join(',') : String(topics);

  useEffect(() => {
    if (!ctx || !enabled) return undefined;
    let timer = null;
    let stale = false;
    const run = () => {
      timer = null;
      if (document.hidden) {
        stale = true;
        return;
      }
      stale = false;
      Promise.resolve()
        .then(() => reloadRef.current && reloadRef.current())
        .catch(() => {});
    };
    const fire = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(run, delay);
    };
    const onVisible = () => {
      if (!document.hidden && stale) run();
    };
    document.addEventListener('visibilitychange', onVisible);
    const off = ctx.subscribe(key.split(','), fire);
    return () => {
      off();
      document.removeEventListener('visibilitychange', onVisible);
      if (timer) clearTimeout(timer);
    };
  }, [ctx, key, delay, enabled]);
}

export function useNotificationEvents(fn) {
  const ctx = useContext(LiveContext);
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!ctx) return undefined;
    return ctx.onNotification((e) => ref.current && ref.current(e));
  }, [ctx]);
}
